import { createEventBus } from './event-bus'
import type { ApplicationEventMap, EventPayload, PayloadType, EventNames } from './types'

// Create a singleton event bus instance
export const eventBus = createEventBus<ApplicationEventMap>({
  debug: process.env.NODE_ENV === 'development'
})

// Export convenience functions for direct use
export const publishEvent = <TEvent extends EventNames<ApplicationEventMap>>(
  eventName: TEvent,
  data: PayloadType<ApplicationEventMap, TEvent>,
  source = 'system'
) => {
  eventBus.publish(eventName, data, source)
}

export const subscribeToEvent = <TEvent extends EventNames<ApplicationEventMap>>(
  eventName: TEvent,
  callback: (data: PayloadType<ApplicationEventMap, TEvent>) => void,
  options = {}
) => {
  return eventBus.subscribe(eventName, (payload) => {
    callback(payload.data)
  }, options)
}

// Re-export the event bus class and types
export * from './event-bus'
export * from './types'

// Register persistent events
eventBus.registerPersistentEvents([
  'ui:themeChange',
  'ui:sidebarToggle',
  'ui:rightSidebarToggle',
  'ui:chatToggle',
  'ui:screenResize',
  'ui:panelStateChange',
  'ui:panelResize',
  'ui:panelMaximize',
  'notification:new',
  'notification:read',
  'notification:clear',
  'notification:count'
])

// Export a type-safe publish helper
export const publishEventSafe = <T extends keyof ApplicationEventMap>(
  eventName: T,
  data: ApplicationEventMap[T]['data'],
  source = 'application',
  metadata?: Record<string, unknown>
) => {
  return eventBus.publish(eventName, data, source, metadata)
}

// Export a type-safe subscribe helper
export const subscribeToEventSafe = <T extends keyof ApplicationEventMap>(
  eventName: T,
  callback: (payload: ApplicationEventMap[T]) => void,
  options?: { priority?: number }
) => {
  return eventBus.subscribe(eventName, callback, options)
} 